/**
 * StatusCard — one card in the OMEGA cockpit grid (S45).
 *
 * A clean white card: a status dot (green/yellow/red/grey), a title, one human
 * sentence, and the whole card is a link to the detail page. Always renders
 * loading / error / value states — never blanks out. Responsive (the grid
 * reflows to one column at 375px).
 */

import { Link } from 'react-router-dom';
import { STATUS_COLORS, type CardState } from './cardStatus';

export interface StatusCardProps {
  title: string;
  /** Detail route the card links to. */
  to: string;
  /** Resolved card state; `undefined` while loading. */
  state: CardState | undefined;
  /** True while the underlying query is loading. */
  loading: boolean;
}

export function StatusCard({ title, to, state, loading }: StatusCardProps) {
  const status = state?.status ?? 'unknown';
  const dot = STATUS_COLORS[status];
  const sentence = loading ? 'Loading…' : (state?.sentence ?? 'Status unavailable.');

  return (
    <Link
      to={to}
      style={{
        display: 'block',
        textDecoration: 'none',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${loading ? STATUS_COLORS.unknown : dot}`,
        borderRadius: 10,
        padding: '16px 18px',
        color: '#111827',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span
          aria-hidden
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: loading ? STATUS_COLORS.unknown : dot,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.01em' }}>{title}</span>
        <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 16, lineHeight: 1 }}>›</span>
      </div>
      <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.45 }}>{sentence}</div>
    </Link>
  );
}
